import React, { useMemo, useState } from 'react';
import {
  Block,
  ExpandLess,
  ExpandMore,
  InfoOutlined,
  MoreVert,
  OpenInNew,
  Print,
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import Box from 'Components/Atoms/Box';
import Button from 'Components/Atoms/Button';
import Checkbox from 'Components/Atoms/Checkbox';
import IconButton from 'Components/Atoms/IconButton';
import TextField from 'Components/Atoms/TextField';
import Modal from 'Components/Molecules/Modal';
import Popover from 'Components/Atoms/Popover';
import Typography from 'Components/Atoms/Typography';
import SignatureDropdown from 'Components/Organisms/SignatureDropdown';
import useAuth from 'CustomHooks/useAuth';
import useDocument from 'CustomHooks/useDocument';
import useLanguage from 'CustomHooks/useLanguage';
import useRestaurant from 'CustomHooks/useRestaurant';
import useMollieNavigation from 'CustomHooks/useMollieNavigation';
import TicketCancellationModal from 'App/ReservationBook/Components/TicketCancellationModal';
import { Reservation } from 'types/reservations';
import { EventTypes } from 'types/shared/documents/restaurants/event';
import type {
  DinnerEventOption,
  Ticket,
} from 'types/shared/documents/restaurants/event';
import rawServer from 'utils/server';
import type { AxiosInstance } from 'axios';
import { actingAs } from 'utils/reservationAuthor';
const server: AxiosInstance = rawServer;
import {
  classHelper,
  getAdditionalInternalFee,
  toCurrencyString,
} from 'utils/helper';
import './styles.scss';
import { apiBaseUrl } from 'api/baseUrl';

import CouponPromise, { currentCouponCalculation } from './CouponPromise';

export type TicketInformationV02Props = {
  reservation: Reservation;
};

type TicketPayment = NonNullable<Ticket['payments']>[number] & {
  name?: string;
  voucherId?: string;
  service_fee?: number;
  reference?: string | null;
  note?: string | null;
  recordedBy?: string | null;
  waiterSignature?: string | null;
  dashboardUrl?: string;
  _links?: {
    dashboard?: {
      href?: string;
    };
  };
};

type PriceLine = {
  id: string;
  label: string;
  total: number;
};

type PaymentInfoLine = {
  label: string;
  value: string;
};

type VoucherValidation = {
  code: string;
  valid: boolean;
  amountLeft: number;
  requestedAmount: number;
  appliedAmount: number;
  remainingAmount: number;
  message?: string;
};

const failedPaymentStatuses = ['canceled', 'failed', 'expired'];
const settledPaymentStatuses = ['paid', 'authorized'];
const onlinePaymentMethods = ['mollie', 'stripe'];
type ManualPaymentType = 'cash' | 'voucher' | 'coupon' | 'external';

const getNumber = (value: unknown) =>
  typeof value === 'number' && !Number.isNaN(value) ? value : 0;

const isActivePayment = (payment: TicketPayment) =>
  !failedPaymentStatuses.includes(String(payment.status));

const isSettledPayment = (payment: TicketPayment) =>
  settledPaymentStatuses.includes(String(payment.status));

const getSettledTicketAmount = (payment: TicketPayment) => {
  if (!isSettledPayment(payment)) return 0;

  const amount = getNumber(payment.amount);

  if (!amount) return 0;

  if (onlinePaymentMethods.includes(String(payment.paidVia))) {
    return Math.max(amount - getNumber(payment.service_fee), 0);
  }

  return amount;
};

const getSettledOnlineFeeImpact = (payment: TicketPayment) => {
  if (!isSettledPayment(payment)) return 0;
  if (!onlinePaymentMethods.includes(String(payment.paidVia))) return 0;
  // Coverage lost to the service-fee subtraction in getSettledTicketAmount(),
  // mirrored exactly: amount - settled = min(service_fee, amount), never > amount.
  return getNumber(payment.amount) - getSettledTicketAmount(payment);
};

const getLocalizedOptionTitle = (
  option: DinnerEventOption | undefined,
  language: string,
  fallback: string
) =>
  (option?.translation as any)?.[language]?.title ??
  option?.translation?.de?.title ??
  option?.internalName ??
  fallback;

const getLocalizedEventTitle = (ticket: Ticket, language: string) =>
  (ticket.event.title as any)?.[language] ??
  ticket.event.title?.de ??
  ticket.event.title?.en ??
  'Ticket';

const latestPayment = (payments: TicketPayment[]) =>
  payments.reduce<TicketPayment | null>((latest, payment) => {
    if (!latest) return payment;
    return (payment.createdAt ?? 0) > (latest.createdAt ?? 0)
      ? payment
      : latest;
  }, null);

const sumPayments = (
  payments: TicketPayment[],
  paidVia: string,
  includePending = true
) =>
  payments
    .filter((payment) => payment.paidVia === paidVia)
    .filter((payment) => includePending || payment.status === 'paid')
    .filter(isActivePayment)
    .reduce((sum, payment) => sum + getNumber(payment.amount), 0);

const getMolliePaymentId = (payment?: TicketPayment | null) =>
  payment?.paymentId ?? payment?.id ?? '';

const getMollieDashboardUrl = (payment?: TicketPayment | null) =>
  payment?.dashboardUrl ?? payment?._links?.dashboard?.href ?? null;

const getMollieInvoiceUrl = (
  restaurantId: string,
  payment?: TicketPayment | null
) => {
  const paymentId = getMolliePaymentId(payment);

  if (!paymentId) return null;

  return `${apiBaseUrl}/v03/mollie/invoice/${restaurantId}/${paymentId}${
    payment?.testmode ? '?testmode=1' : ''
  }`;
};

const getPaymentMethodLabel = (method: string, t: (key: string) => string) => {
  switch (method) {
    case 'mollie':
      return 'Mollie';
    case 'stripe':
      return 'Stripe';
    case 'cash':
      return t('Cash');
    case 'voucher':
      return t('Voucher');
    case 'coupon':
      return t('Coupon');
    case 'external':
      return t('Paid outside Gastronaut');
    default:
      return method || t('Unknown payment method');
  }
};

const getCompactStatusLabel = (statusKey: string, t: (key: string) => string) => {
  switch (statusKey) {
    case 'success':
      return t('Paid short');
    case 'failed':
      return t('Failed short');
    case 'processing':
      return t('Processing short');
    case 'in-house':
      return t('Restaurant short');
    default:
      return t('Open short');
  }
};

const getOpenPaymentDescription = ({
  paymentStatus,
  ticketStatus,
  t,
}: {
  paymentStatus?: string;
  ticketStatus?: string;
  t: (key: string) => string;
}) => {
  if (paymentStatus) return t(paymentStatus);

  if (
    !ticketStatus ||
    ticketStatus === 'payments' ||
    ticketStatus === 'missing_payments'
  ) {
    return t('No payment recorded');
  }

  return t(ticketStatus);
};

const getStatusInfo = ({
  ticket,
  latestRelevantPayment,
  t,
}: {
  ticket: Ticket;
  latestRelevantPayment: TicketPayment | null;
  t: (key: string) => string;
}) => {
  const paymentStatus = latestRelevantPayment?.status;

  if (ticket.payAtRestaurant) {
    return {
      key: 'in-house',
      label: t('Payment at restaurant'),
      description: t('Payment has to be collected in house'),
    };
  }

  if (ticket.status === 'processing' || paymentStatus === 'pending') {
    return {
      key: 'processing',
      label: t('Payment processing'),
      description: t(paymentStatus ?? ticket.status),
    };
  }

  if (
    ticket.status === 'succedded' ||
    paymentStatus === 'paid' ||
    paymentStatus === 'authorized' ||
    ticket.paid
  ) {
    return {
      key: 'success',
      label: t('Payment successful'),
      description: t(paymentStatus ?? 'paid'),
    };
  }

  if (
    ticket.status === 'failed' ||
    failedPaymentStatuses.includes(String(paymentStatus))
  ) {
    return {
      key: 'failed',
      label: t('Payment failed'),
      description: t(paymentStatus ?? ticket.status),
    };
  }

  return {
    key: 'open',
    label: t('Payment open'),
    description: getOpenPaymentDescription({
      paymentStatus,
      ticketStatus: ticket.status,
      t,
    }),
  };
};

const TicketInformationV02: React.FC<TicketInformationV02Props> = ({
  reservation,
}) => {
  const { t } = useTranslation('reservations');
  const { language } = useLanguage();
  const { uid, roleMap } = useAuth();
  const mollieNavigation = useMollieNavigation();
  const { reservationSettings, experimentalFlags } = useRestaurant();
  const [ticketDocument] = useDocument<Ticket>(
    `/ticketsV02`,
    reservation.ticketId ?? ''
  );

  const ticket = ticketDocument.data;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [manualPaymentType, setManualPaymentType] =
    useState<ManualPaymentType>('cash');
  const [manualPaymentAmount, setManualPaymentAmount] = useState('');
  const [manualPaymentReference, setManualPaymentReference] = useState('');
  const [manualPaymentNote, setManualPaymentNote] = useState('');
  const [manualPaymentSignature, setManualPaymentSignature] = useState<
    string | null
  >(null);
  const [manualPaymentSaving, setManualPaymentSaving] = useState(false);
  const [manualPaymentError, setManualPaymentError] = useState<string | null>(
    null
  );
  const [voucherValidation, setVoucherValidation] =
    useState<VoucherValidation | null>(null);
  const [voucherValidationLoading, setVoucherValidationLoading] =
    useState(false);
  const [remainderPaymentType, setRemainderPaymentType] =
    useState<Exclude<ManualPaymentType, 'voucher'>>('cash');
  const [remainderPaymentReference, setRemainderPaymentReference] =
    useState('');
  const [remainderPaymentNote, setRemainderPaymentNote] = useState('');
  const [paymentInfoAnchor, setPaymentInfoAnchor] =
    useState<HTMLElement | null>(null);
  const [paymentInfoLines, setPaymentInfoLines] = useState<PaymentInfoLine[]>(
    []
  );
  const [keepModalOpen, setKeepModalOpen] = useState(false);
  const [keepSaving, setKeepSaving] = useState(false);
  const [keepError, setKeepError] = useState<string | null>(null);
  const [cancellationError, setCancellationError] = useState<string | null>(null);
  const [sendingCancellationEmail, setSendingCancellationEmail] = useState(false);
  const [cancellationModalOpen, setCancellationModalOpen] = useState(false);

  const {
    lineItems,
    subtotal,
    couponAmount,
    voucherAmount,
    serviceFee,
    total,
    onlineDue,
    remainingAtRestaurant,
    pendingAmount,
    paymentMethod,
    statusInfo,
    molliePaymentUrl,
    hasMollieDashboardFallback,
    mollieInvoiceUrl,
    couponPaymentDetails,
  } = useMemo(() => {
    const emptyState = {
      lineItems: [] as PriceLine[],
      subtotal: 0,
      couponAmount: 0,
      voucherAmount: 0,
      serviceFee: 0,
      total: 0,
      onlineDue: 0,
      remainingAtRestaurant: 0,
      pendingAmount: 0,
      paymentMethod: '',
      statusInfo: {
        key: 'open',
        label: '',
        description: '',
      },
      molliePaymentUrl: null as string | null,
      hasMollieDashboardFallback: false,
      mollieInvoiceUrl: null as string | null,
      couponPaymentDetails: [] as TicketPayment[],
    };

    if (!ticket) return emptyState;

    const payments = ticket.payments ?? [];
    const activeOnlinePayments = payments.filter(
      (payment) =>
        onlinePaymentMethods.includes(String(payment.paidVia)) &&
        isActivePayment(payment)
    );
    const allOnlinePayments = payments.filter((payment) =>
      onlinePaymentMethods.includes(String(payment.paidVia))
    );
    const nonCouponPayments = payments.filter(
      (payment) => String(payment.paidVia) !== 'coupon'
    );
    const latestOnlinePayment =
      latestPayment(activeOnlinePayments) ?? latestPayment(allOnlinePayments);
    const latestRelevantPayment =
      latestOnlinePayment ?? latestPayment(nonCouponPayments);
    const latestMolliePayment =
      latestPayment(
        activeOnlinePayments.filter(
          (payment) => String(payment.paidVia) === 'mollie'
        )
      ) ??
      latestPayment(
        allOnlinePayments.filter(
          (payment) => String(payment.paidVia) === 'mollie'
        )
      );

    const lineItems: PriceLine[] = [];

    if (ticket.event.type === EventTypes.DINNER_TICKET) {
      const optionMap = new Map(
        (ticket.event.options ?? []).map((option) => [option.id, option])
      );
      const addOnMap = new Map(
        (ticket.event.optionalAddOns ?? []).map((option) => [
          option.id,
          option,
        ])
      );

      const orderedOptionIds = [
        ...(ticket.event.options ?? []).map((option) => option.id),
        ...Object.keys(ticket.options ?? {}).filter(
          (id) => !optionMap.has(id)
        ),
      ];

      orderedOptionIds.forEach((id) => {
        const quantity = getNumber(ticket.options?.[id]);
        if (quantity <= 0) return;

        const option = optionMap.get(id);
        const unitPrice = getNumber(ticket.event.amount) + getNumber(option?.amount);

        lineItems.push({
          id: `option-${id}`,
          label: `${quantity}x ${getLocalizedOptionTitle(
            option,
            language,
            id
          )}`,
          total: unitPrice * quantity,
        });
      });

      const orderedAddOnIds = [
        ...(ticket.event.optionalAddOns ?? []).map((option) => option.id),
        ...Object.keys(ticket.optionalAddOns ?? {}).filter(
          (id) => !addOnMap.has(id)
        ),
      ];

      orderedAddOnIds.forEach((id) => {
        const quantity = getNumber(ticket.optionalAddOns?.[id]);
        if (quantity <= 0) return;

        const option = addOnMap.get(id);

        lineItems.push({
          id: `addon-${id}`,
          label: `${quantity}x ${getLocalizedOptionTitle(
            option,
            language,
            id
          )}`,
          total: getNumber(option?.amount) * quantity,
        });
      });
    }

    if (!lineItems.length && ticket.amount) {
      lineItems.push({
        id: 'ticket',
        label: getLocalizedEventTitle(ticket, language),
        total: ticket.amount,
      });
    }

    const subtotal = getNumber(ticket.amount);
    const couponAmount = sumPayments(payments, 'coupon');
    const voucherAmount = sumPayments(payments, 'voucher');
    const couponPaymentDetails = payments
      .filter((payment) => String(payment.paidVia) === 'coupon')
      .filter(isActivePayment)
      .filter((payment) => getNumber(payment.amount) > 0);
    const serviceFee = getNumber(latestOnlinePayment?.service_fee);
    const coveredAmount = payments.reduce(
      (sum, payment) => sum + getSettledTicketAmount(payment),
      0
    );
    const partialPayment = Math.min(
      Math.max(getNumber(ticket.partialPayment) || 1, 0.01),
      1
    );
    const requiredOnlineBase = subtotal * partialPayment;
    const total = Math.max(subtotal - couponAmount - voucherAmount + serviceFee, 0);
    // Coupon first: the deposit was taken from the reduced price, so the
    // promised calculation, not the share of the full price, is the claim.
    const couponCalculation = currentCouponCalculation(ticket);
    const onlineDue = ticket.payAtRestaurant
      ? 0
      : couponCalculation
        ? Math.max(couponCalculation.onlineDue - voucherAmount + serviceFee, 0)
        : Math.max(
            requiredOnlineBase - couponAmount - voucherAmount + serviceFee,
            0
          );
    const remainingAtRestaurant = ticket.payAtRestaurant
      ? total
      : couponCalculation
        ? couponCalculation.later
        : Math.max(subtotal - requiredOnlineBase, 0);
    const rawPendingAmount = Math.max(subtotal - coveredAmount, 0);
    const settledOnlineFeeImpact = payments.reduce(
      (sum, payment) => sum + getSettledOnlineFeeImpact(payment),
      0
    );
    // Online payments persisted net (pre-2026-06-12) understate coverage by up to their
    // service fee; subtract the summed online fee impact so a phantom gap collapses to 0
    // while any genuine balance beyond the fee artifact is preserved. Backend paid/status
    // stays authoritative.
    // Only collapse a gap that is ENTIRELY a net-vs-gross fee artifact (pre-2026-06-12
    // payments stored net). A genuine remaining balance — e.g. a gross-stored partial
    // deposit where the rest is still owed — exceeds the summed online fee impact and is
    // left untouched, so it is never understated by the service fee.
    const pendingAmount =
      rawPendingAmount <= settledOnlineFeeImpact + 0.009 ? 0 : rawPendingAmount;

    const methodPayments = payments
      .filter(isActivePayment)
      .filter((payment) => String(payment.paidVia) !== 'coupon');
    const paymentMethodSources =
      methodPayments.length || !latestRelevantPayment
        ? methodPayments
        : [latestRelevantPayment];
    const paymentMethods = Array.from(
      new Set(
        paymentMethodSources
          .map((payment) => String(payment.paidVia))
          .filter(Boolean)
      )
    );

    const paymentMethod = paymentMethods.length
      ? paymentMethods
          .map((method) => getPaymentMethodLabel(method, t))
          .join(' + ')
      : ticket.payAtRestaurant
        ? t('Restaurant short')
        : t('No payment method');

    const statusInfo =
      pendingAmount <= 0.009
        ? {
            key: 'success',
            label: t('Payment successful'),
            description: t('paid'),
          }
        : getStatusInfo({
            ticket,
            latestRelevantPayment,
            t,
          });

    return {
      lineItems,
      subtotal,
      couponAmount,
      voucherAmount,
      serviceFee,
      total,
      onlineDue,
      remainingAtRestaurant,
      pendingAmount,
      paymentMethod,
      statusInfo,
      molliePaymentUrl: latestMolliePayment
        ? getMollieDashboardUrl(latestMolliePayment)
        : null,
      hasMollieDashboardFallback: !!latestMolliePayment && !!uid,
      mollieInvoiceUrl: getMollieInvoiceUrl(
        ticket.restaurant,
        latestMolliePayment
      ),
      couponPaymentDetails,
    };
  }, [language, t, ticket, uid]);

  if (!ticket) {
    return <></>;
  }

  const internalFeeConfig =
    experimentalFlags.data?.additionalInternalGastronautFee;
  const hideServiceFee = !!internalFeeConfig?.value;
  const internalGastronautFee = hideServiceFee
    ? getAdditionalInternalFee(subtotal, internalFeeConfig)
    : 0;
  const totalAfterInternalFee = Math.max(
    total - internalGastronautFee - (hideServiceFee ? serviceFee : 0),
    0
  );
  const hasPriceAdjustments =
    couponAmount > 0 ||
    voucherAmount > 0 ||
    (serviceFee > 0 && !hideServiceFee) ||
    internalGastronautFee > 0;
  const showPartialRows =
    !ticket.payAtRestaurant &&
    (remainingAtRestaurant > 0 || Math.abs(onlineDue - total) > 0.01);
  const hasMollieActions = !!molliePaymentUrl || hasMollieDashboardFallback || !!mollieInvoiceUrl;
  const showPaymentMethod =
    !!paymentMethod &&
    paymentMethod !== statusInfo.label &&
    paymentMethod !== t('No payment method');
  const cancellation = ticket.cancellation;
  const showPendingPaymentWarning = !cancellation && pendingAmount > 0.009;
  const compactStatusKey = cancellation
    ? 'cancelled'
    : showPendingPaymentWarning
      ? 'open'
      : statusInfo.key;
  const compactStatusLabel = cancellation
    ? t('Cancelled short')
    : showPendingPaymentWarning
      ? t('Open short')
      : getCompactStatusLabel(statusInfo.key, t);
  const compactStatusDescription = cancellation
    ? ''
    : showPendingPaymentWarning
      ? toCurrencyString(pendingAmount, '€', false, 1)
      : statusInfo.key === 'success'
        ? ''
        : statusInfo.description;
  const compactOptionLines = lineItems.slice(0, 4);
  const hiddenOptionCount = Math.max(
    lineItems.length - compactOptionLines.length,
    0
  );
  const signatureRequired = !!reservationSettings?.signatureRequired;
  const pendingReservationValidTill =
    !cancellation && reservation.validTill && reservation.validTill > Date.now()
      ? reservation.validTill
      : null;
  const formatDateTime = (timestamp: number) =>
    new Date(timestamp).toLocaleString(
      language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : 'en-US',
      {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }
    );
  const formattedValidTill = pendingReservationValidTill
    ? formatDateTime(pendingReservationValidTill)
    : '';
  // While the guest is still paying, the booking belongs to the payment flow.
  const canCancelTicket =
    !pendingReservationValidTill &&
    (roleMap.admin || roleMap.chefWaiter);
  const refundVoucherCode = reservation.ticketRefund?.voucherCode ?? null;
  const refundVoucherEmailedTo = reservation.ticketRefund?.voucherEmailedTo ?? null;
  const refundSummary = (() => {
    const refund = cancellation?.refund;

    if (!refund || refund.method === 'none' || !refund.amount) return t('No refund');

    const amount = toCurrencyString(refund.amount, '€', false, 1);

    if (refund.status && refund.status !== 'refunded') return t('Refund is not yet confirmed: {{amount}}', { amount });
    if (refund.method === 'originalVoucher') return t('Restored to the original voucher: {{amount}}', { amount });
    return refund.method === 'mollie'
      ? `${t('Refunded via Mollie: {{amount}}', { amount })} · ${t(
          'the guest sees it after a few business days'
        )}`
      : t('Refunded as a voucher: {{amount}}', { amount });
  })();
  const voucherNeedsRemainder =
    manualPaymentType === 'voucher' &&
    !!voucherValidation &&
    voucherValidation.remainingAmount > 0.009;

  const resetVoucherFlow = () => {
    setVoucherValidation(null);
    setVoucherValidationLoading(false);
    setRemainderPaymentType('cash');
    setRemainderPaymentReference('');
    setRemainderPaymentNote('');
  };

  const openManualPaymentModal = () => {
    setManualPaymentType('cash');
    setManualPaymentAmount(pendingAmount.toFixed(2));
    setManualPaymentReference('');
    setManualPaymentNote('');
    setManualPaymentSignature(null);
    setManualPaymentError(null);
    resetVoucherFlow();
    setPaymentModalOpen(true);
  };

  const closeManualPaymentModal = () => {
    if (manualPaymentSaving) return;

    setPaymentModalOpen(false);
    setManualPaymentError(null);
    resetVoucherFlow();
  };

  const validateManualVoucher = async () => {
    if (!ticket || !uid || !reservation.id) return null;

    const code = manualPaymentReference.trim();
    const amount = Number(String(manualPaymentAmount).replace(',', '.'));

    if (!code) {
      setManualPaymentError(t('Voucher code is required'));
      return null;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      setManualPaymentError(t('Payment amount is invalid'));
      return null;
    }

    setVoucherValidationLoading(true);
    setManualPaymentError(null);

    try {
      const { data } = await server.get<Omit<VoucherValidation, 'code'>>(
        `/v03/reservations/${ticket.restaurant}/res/${reservation.id}/ticketVoucher/${encodeURIComponent(
          code
        )}?uid=${encodeURIComponent(uid)}&amount=${encodeURIComponent(amount)}`
      );
      const validation = {
        ...data,
        code,
      };

      setVoucherValidation(validation);
      return validation;
    } catch (error: any) {
      const message = error?.response?.data?.message ?? error?.message;

      setVoucherValidation(null);
      setManualPaymentError(
        typeof message === 'string' ? message : t('Voucher could not be validated')
      );
      return null;
    } finally {
      setVoucherValidationLoading(false);
    }
  };

  const openPaymentInfo = (
    event: React.MouseEvent<HTMLElement>,
    lines: PaymentInfoLine[]
  ) => {
    event.stopPropagation();
    setPaymentInfoAnchor(event.currentTarget);
    setPaymentInfoLines(lines);
  };

  const openCouponInfo = (event: React.MouseEvent<HTMLElement>) => {
    const lines = couponPaymentDetails.flatMap((payment, index) => {
      const paymentLines: PaymentInfoLine[] = [
        {
          label:
            couponPaymentDetails.length > 1
              ? `${t('Discount')} ${index + 1}`
              : t('Discount'),
          value: toCurrencyString(getNumber(payment.amount), '€', false, 1),
        },
      ];

      if (payment.couponCode) {
        paymentLines.push({
          label: t('Code'),
          value: payment.couponCode,
        });
      }

      if (payment.reference) {
        paymentLines.push({
          label: t('Reference'),
          value: payment.reference,
        });
      }

      if (payment.note) {
        paymentLines.push({
          label: t('Note'),
          value: payment.note,
        });
      }

      return paymentLines;
    });

    openPaymentInfo(event, lines);
  };

  const handleKeepReservation = async () => {
    if (!ticket || !uid || !reservation.id) return;

    setKeepSaving(true);
    setKeepError(null);

    try {
      const { data } = await server.post<{
        confirmationEmail?: {
          status?: string;
          message?: {
            de?: string;
            en?: string;
          };
        };
      }>(
        `/v03/reservations/${ticket.restaurant}/res/${reservation.id}/keepTicketReservation`,
        { uid }
      );

      if (data.confirmationEmail?.status !== 'email_sent') {
        setKeepError(
          data.confirmationEmail?.message?.[language === 'de' ? 'de' : 'en'] ??
            t('Reservation was kept but confirmation email could not be sent')
        );
        return;
      }

      setKeepModalOpen(false);
    } catch (error: any) {
      const message = error?.response?.data?.message ?? error?.message;

      setKeepError(
        typeof message === 'string'
          ? message
          : t('Reservation could not be kept')
      );
    } finally {
      setKeepSaving(false);
    }
  };

  const handleManualPaymentSubmit = async () => {
    if (!ticket || !uid || !reservation.id) return;

    const amount = Number(String(manualPaymentAmount).replace(',', '.'));
    const trimmedReference = manualPaymentReference.trim();
    const trimmedNote = manualPaymentNote.trim();

    if (!Number.isFinite(amount) || amount <= 0) {
      setManualPaymentError(t('Payment amount is invalid'));
      return;
    }

    if (manualPaymentType === 'voucher' && !trimmedReference) {
      setManualPaymentError(t('Voucher code is required'));
      return;
    }

    if (
      manualPaymentType !== 'voucher' &&
      !trimmedReference &&
      !trimmedNote
    ) {
      setManualPaymentError(t('Reference or note is required'));
      return;
    }

    let currentVoucherValidation = voucherValidation;

    if (manualPaymentType === 'voucher') {
      const validationIsCurrent =
        voucherValidation?.code === trimmedReference &&
        Math.abs(voucherValidation.requestedAmount - amount) < 0.01;

      currentVoucherValidation = validationIsCurrent
        ? voucherValidation
        : await validateManualVoucher();

      if (!currentVoucherValidation?.valid) return;

      if (currentVoucherValidation.remainingAmount > 0.009) {
        const remainderReference = remainderPaymentReference.trim();
        const remainderNote = remainderPaymentNote.trim();

        if (!remainderReference && !remainderNote) {
          setManualPaymentError(t('Reference or note is required'));
          return;
        }
      }
    }

    if (signatureRequired && !manualPaymentSignature) {
      setManualPaymentError(t('Signature is required'));
      return;
    }

    setManualPaymentSaving(true);
    setManualPaymentError(null);

    try {
      await server.post(
        `/v03/reservations/${ticket.restaurant}/res/${reservation.id}/ticketPayment`,
        {
          uid,
          type: manualPaymentType,
          amount,
          reference: trimmedReference || null,
          note: trimmedNote || null,
          waiterSignature:
            manualPaymentSignature || actingAs(ticket.restaurant, null) || null,
          additionalPayment:
            manualPaymentType === 'voucher' &&
            currentVoucherValidation &&
            currentVoucherValidation.remainingAmount > 0.009
              ? {
                  type: remainderPaymentType,
                  amount: currentVoucherValidation.remainingAmount,
                  reference: remainderPaymentReference.trim() || null,
                  note: remainderPaymentNote.trim() || null,
                }
              : null,
        }
      );
      setPaymentModalOpen(false);
      resetVoucherFlow();
    } catch (error: any) {
      const message = error?.response?.data?.message ?? error?.message;

      setManualPaymentError(
        typeof message === 'string'
          ? message
          : t('Payment could not be recorded')
      );
    } finally {
      setManualPaymentSaving(false);
    }
  };

  return (
    <div className="ticket-information-v02">
      <Box className="ticket-information-v02__snapshot">
        <Box className="ticket-information-v02__snapshot-header">
          <Box className="ticket-information-v02__snapshot-title-row">
            <Typography
              className="colon"
              variant="text-3"
              translation="reservations"
              color="secondary"
            >
              Ticket
            </Typography>
            <Box
              className={classHelper([
                'ticket-information-v02__status-pill',
                `ticket-information-v02__status-pill--${compactStatusKey}`,
              ])}
            >
              <Typography variant="text-4" translation={null}>
                {compactStatusLabel}
              </Typography>
              {!!compactStatusDescription && (
                <Typography variant="text-4" translation={null}>
                  {compactStatusDescription}
                </Typography>
              )}
            </Box>
          </Box>
          <Box className="ticket-information-v02__snapshot-copy">
            <Typography
              variant="text-4"
              color="subdued"
              translation={null}
              block
              className="ticket-information-v02__event-title"
            >
              {getLocalizedEventTitle(ticket, language)}
            </Typography>
          </Box>
        </Box>

        <Box className="ticket-information-v02__option-list">
          {compactOptionLines.map((line) => (
            <Box key={line.id} className="ticket-information-v02__option-line">
              <Typography variant="text-3" translation={null}>
                {line.label}
              </Typography>
            </Box>
          ))}
          {!!hiddenOptionCount && (
            <Typography
              variant="text-4"
              color="subdued"
              translation={null}
              className="ticket-information-v02__option-more"
            >
              +{hiddenOptionCount}
            </Typography>
          )}
        </Box>

        {!!cancellation && (
          <Box className="ticket-information-v02__cancellation">
            {!!cancellationError && <Typography translation={null} block>{cancellationError}</Typography>}
            <Typography variant="text-3" translation="reservations" block>
              {cancellation.status === 'completed'
                ? 'Ticket cancelled'
                : 'Ticket cancellation in progress'}
            </Typography>
            {(
              <>
                <Typography variant="text-4" translation={null} block>
                  {[
                    cancellation.completedAt
                      ? formatDateTime(cancellation.completedAt)
                      : '',
                    cancellation.signature,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
                <Typography variant="text-4" translation={null} block>
                  {refundSummary}
                </Typography>
                {!!reservation.ticketRefund?.lateRefundAmount && (
                  <Typography variant="text-4" translation={null} block>
                    {t('Later payment refunded: {{amount}}', { amount: toCurrencyString(reservation.ticketRefund.lateRefundAmount, '€', false, 1) })}
                  </Typography>
                )}
                {!!reservation.ticketRefund?.lateRefundPendingAmount && (
                  <Typography variant="text-4" translation={null} block>
                    {t('Later payment refund not yet confirmed: {{amount}}', { amount: toCurrencyString(reservation.ticketRefund.lateRefundPendingAmount, '€', false, 1) })}
                  </Typography>
                )}
                {!!cancellation.reason && (
                  <Typography variant="text-4" translation={null} block>
                    {t('Reason')}: {cancellation.reason}
                  </Typography>
                )}
                {!!cancellation.refund?.error && (
                  <Typography
                    variant="text-4"
                    translation={null}
                    className="ticket-information-v02__cancellation-error"
                    block
                  >
                    {cancellation.refund.error}
                  </Typography>
                )}
                {!!refundVoucherCode && (
                  <Typography
                    variant="text-4"
                    translation={null}
                    className={
                      refundVoucherEmailedTo
                        ? undefined
                        : 'ticket-information-v02__cancellation-error'
                    }
                    block
                  >
                    {refundVoucherEmailedTo
                      ? t('Voucher {{code}} was emailed to {{email}}', {
                          code: refundVoucherCode,
                          email: refundVoucherEmailedTo,
                        })
                      : t(reservation.ticketRefund?.emailStatus === 'failed' ? 'Voucher {{code}} created. Email failed.' : reservation.guest?.email ? 'Voucher {{code}} created. Email delivery is still pending.' : 'Voucher {{code}}: hand it to the guest, there is no email address', { code: refundVoucherCode })}
                  </Typography>
                )}
                {!!refundVoucherCode && reservation.ticketRefund?.emailStatus === 'failed' && canCancelTicket && (
                  <Button translation={null} disabled={sendingCancellationEmail} loading={sendingCancellationEmail} onClick={async () => {
                    if (sendingCancellationEmail) return;
                    setSendingCancellationEmail(true);
                    setCancellationError(null);
                    try { await server.post(`/v03/reservations/${ticket.restaurant}/res/${reservation.id}/ticketCancellation/email`); }
                    catch (err: any) { setCancellationError(err?.response?.data?.message ?? err.message); }
                    finally { setSendingCancellationEmail(false); }
                  }}>{t('Retry voucher email')}</Button>
                )}
                {!!cancellation.refund?.documentNumber && canCancelTicket && <Button translation={null} onClick={async () => {
                  try {
                    const { data } = await server.get(`/v03/reservations/${ticket.restaurant}/res/${reservation.id}/ticketCancellation/pdf`, { responseType: 'blob' });
                    const url = URL.createObjectURL(data); const anchor = document.createElement('a');
                    anchor.href = url; anchor.download = `${cancellation.refund?.documentNumber}.pdf`; anchor.click();
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                  } catch (err: any) { setCancellationError(err?.response?.data?.message ?? err.message); }
                }}>{t('Download cancellation receipt')}</Button>}
                {!!refundVoucherCode && (
                  <Button
                    variant="transparent"
                    translation={null}
                    href={`/${ticket.restaurant}/vouchers?code=${encodeURIComponent(
                      refundVoucherCode
                    )}`}
                    target="_blank"
                    startIcon={(props) => <OpenInNew {...props} />}
                    className="ticket-information-v02__cancellation-voucher"
                    typographyProps={{ variant: 'text-4' }}
                  >
                    {t('Open voucher {{code}}', { code: refundVoucherCode })}
                  </Button>
                )}
              </>
            )}
          </Box>
        )}

        {pendingReservationValidTill && (
          <Box className="ticket-information-v02__expiration-warning">
            <Box className="ticket-information-v02__pending-copy">
              <Typography variant="text-3" translation="reservations" block>
                Pending reservation
              </Typography>
              <Typography variant="text-4" translation={null} block>
                {t('This reservation will be deleted at', {
                  time: formattedValidTill,
                })}
              </Typography>
            </Box>
            <Button
              variant="secondary"
              translation="reservations"
              onClick={() => {
                setKeepError(null);
                setKeepModalOpen(true);
              }}
              className="ticket-information-v02__pending-action"
              typographyProps={{ variant: 'text-4' }}
            >
              Keep reservation
            </Button>
          </Box>
        )}

        {showPendingPaymentWarning && (
          <Box className="ticket-information-v02__pending-payment">
            <Box className="ticket-information-v02__pending-copy">
              <Typography variant="text-3" translation="reservations" block>
                Open ticket amount
              </Typography>
              <Typography variant="text-4" translation={null} block>
                {toCurrencyString(pendingAmount, '€', false, 1)}{' '}
                {t('must be collected at the restaurant')}
              </Typography>
            </Box>
            <Button
              variant="secondary"
              translation="reservations"
              onClick={openManualPaymentModal}
              className="ticket-information-v02__pending-action"
              typographyProps={{ variant: 'text-4' }}
            >
              Add payment
            </Button>
          </Box>
        )}

        <Button
          variant="transparent"
          translation="reservations"
          className="ticket-information-v02__details-toggle"
          typographyProps={{ variant: 'text-4' }}
          endIcon={(props) =>
            detailsOpen ? <ExpandLess {...props} /> : <ExpandMore {...props} />
          }
          aria-expanded={detailsOpen}
          onClick={() => setDetailsOpen((open) => !open)}
        >
          {detailsOpen ? 'Hide price details' : 'Show price details'}
        </Button>
      </Box>

      {detailsOpen && (
        <Box className="ticket-information-v02__details" component="div">
          <Box
            className={classHelper([
              'ticket-information-v02__status',
              `ticket-information-v02__status--${statusInfo.key}`,
            ])}
          >
            <Box className="ticket-information-v02__status-header">
              <Box className="ticket-information-v02__status-copy">
                <Typography variant="text-3" translation={null} block>
                  {statusInfo.label}
                </Typography>
                <Typography variant="text-4" translation={null} block>
                  {statusInfo.description}
                </Typography>
              </Box>
              {hasMollieActions && (
                <IconButton
                  size="x-small"
                  color="subdued"
                  className="ticket-information-v02__status-menu"
                  aria-label={t('Payment actions')}
                  onClick={(event) => {
                    event.stopPropagation();
                    setActionsAnchor((anchor) =>
                      anchor ? null : event.currentTarget
                    );
                  }}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
              )}
            </Box>
            {showPaymentMethod && (
              <Box className="ticket-information-v02__status-method">
                <Typography
                  variant="text-4"
                  translation="reservations"
                  color="subdued"
                >
                  Payment Method
                </Typography>
                <Typography variant="text-4" translation={null}>
                  {paymentMethod}
                </Typography>
              </Box>
            )}
            {hasMollieActions && (
              <Popover
                anchorEl={actionsAnchor}
                open={!!actionsAnchor}
                onClose={() => setActionsAnchor(null)}
                placement="bottom-end"
                padding={0}
                width={220}
              >
                <Box className="ticket-information-v02__actions-popover">
                  {(molliePaymentUrl || hasMollieDashboardFallback) && (
                    <Button
                      fullWidth
                      variant="transparent"
                      href={molliePaymentUrl ?? undefined}
                      target="_blank"
                      disabled={mollieNavigation.opening}
                      loading={mollieNavigation.opening}
                      startIcon={(props) => <OpenInNew {...props} />}
                      translation="reservations"
                      className="ticket-information-v02__popover-action"
                      typographyProps={{ variant: 'text-4' }}
                      onClick={() => {
                        if (!molliePaymentUrl) {
                          mollieNavigation.open(ticket.restaurant, 'dashboard', true);
                        }
                        setActionsAnchor(null);
                      }}
                    >
                      Open Mollie payment
                    </Button>
                  )}
                  {mollieInvoiceUrl && (
                    <Button
                      fullWidth
                      variant="transparent"
                      href={mollieInvoiceUrl}
                      target="_blank"
                      startIcon={(props) => <Print {...props} />}
                      translation="reservations"
                      className="ticket-information-v02__popover-action"
                      typographyProps={{ variant: 'text-4' }}
                      onClick={() => setActionsAnchor(null)}
                    >
                      Print invoice
                    </Button>
                  )}
                </Box>
              </Popover>
            )}
          </Box>

          <Box className="ticket-information-v02__table" component="div">
          {lineItems.map((line) => (
            <Box key={line.id} className="ticket-information-v02__row">
              <Typography
                variant="text-3"
                translation={null}
                className="ticket-information-v02__line-label"
              >
                {line.label}
              </Typography>
              <Typography variant="text-2" color="subdued" translation={null}>
                {toCurrencyString(line.total, '€', false, 1)}
              </Typography>
            </Box>
          ))}

          {hasPriceAdjustments && (
            <Box className="ticket-information-v02__row ticket-information-v02__subtotal">
              <Typography variant="text-3" translation="reservations">
                Subtotal
              </Typography>
              <Typography variant="text-2" color="subdued" translation={null}>
                {toCurrencyString(subtotal, '€', false, 1)}
              </Typography>
            </Box>
          )}

          {couponAmount > 0 && (
            <Box className="ticket-information-v02__row ticket-information-v02__discount">
              <Box className="ticket-information-v02__label-with-icon">
                <Typography variant="text-3" translation="reservations">
                  Coupon
                </Typography>
                {!!couponPaymentDetails.length && (
                  <IconButton
                    size="x-small"
                    color="subdued"
                    aria-label={t('Show payment reference')}
                    title={t('Show payment reference')}
                    className="ticket-information-v02__info-button"
                    onMouseEnter={openCouponInfo}
                    onClick={openCouponInfo}
                  >
                    <InfoOutlined fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Typography variant="text-2" translation={null}>
                - {toCurrencyString(couponAmount, '€', false, 1)}
              </Typography>
            </Box>
          )}

          {voucherAmount > 0 && (
            <Box className="ticket-information-v02__row ticket-information-v02__discount">
              <Typography variant="text-3" translation="reservations">
                Voucher
              </Typography>
              <Typography variant="text-2" translation={null}>
                - {toCurrencyString(voucherAmount, '€', false, 1)}
              </Typography>
            </Box>
          )}

          {serviceFee > 0 && !hideServiceFee && (
            <Box className="ticket-information-v02__row">
              <Typography variant="text-3" translation="reservations">
                Service fee
              </Typography>
              <Typography variant="text-2" color="subdued" translation={null}>
                + {toCurrencyString(serviceFee, '€', false, 1)}
              </Typography>
            </Box>
          )}

          {internalGastronautFee > 0 && (
            <Box className="ticket-information-v02__row">
              <Typography variant="text-3" translation="reservations">
                Gastronaut commission
              </Typography>
              <Typography variant="text-2" color="subdued" translation={null}>
                - {toCurrencyString(internalGastronautFee, '€', false, 1)}
              </Typography>
            </Box>
          )}

          <Box className="ticket-information-v02__row ticket-information-v02__total">
            <Typography variant="text-3" translation="reservations">
              Total
            </Typography>
            <Typography variant="text-2" translation={null}>
              {toCurrencyString(totalAfterInternalFee, '€', false, 1)}
            </Typography>
          </Box>

          {showPartialRows && (
            <>
              <Box className="ticket-information-v02__row ticket-information-v02__subrow">
                <Typography variant="text-4" translation="reservations">
                  Guest pays online
                </Typography>
                <Typography variant="text-3" color="subdued" translation={null}>
                  {toCurrencyString(onlineDue, '€', false, 1)}
                </Typography>
              </Box>
              <Box className="ticket-information-v02__row ticket-information-v02__subrow">
                <Typography variant="text-4" translation="reservations">
                  Remaining at restaurant
                </Typography>
                <Typography variant="text-3" color="subdued" translation={null}>
                  {toCurrencyString(remainingAtRestaurant, '€', false, 1)}
                </Typography>
              </Box>
            </>
          )}
          <CouponPromise reservation={reservation} ticket={ticket} />
          </Box>
        </Box>
      )}

      {canCancelTicket && (
        <Box className="ticket-information-v02__cancel-row">
          <Button
            variant="transparent"
            translation="reservations"
            className="ticket-information-v02__cancel-button"
            startIcon={(props) => <Block {...props} />}
            typographyProps={{ variant: 'text-4' }}
            onClick={() => setCancellationModalOpen(true)}
          >
            {cancellation ? t('Check cancellation again') : t('Cancel ticket')}
          </Button>
        </Box>
      )}

      {cancellationModalOpen && (
        <TicketCancellationModal
          restaurantId={ticket.restaurant}
          reservation={reservation}
          onClose={() => setCancellationModalOpen(false)}
        />
      )}

      <Popover
        anchorEl={paymentInfoAnchor}
        open={!!paymentInfoAnchor}
        onClose={() => setPaymentInfoAnchor(null)}
        placement="bottom-start"
        padding={0}
        width={260}
      >
        <Box className="ticket-information-v02__info-popover">
          {paymentInfoLines.map((line, index) => (
            <Box
              key={`${line.label}-${index}`}
              className="ticket-information-v02__info-line"
            >
              <Typography variant="text-4" translation={null} color="subdued">
                {line.label}
              </Typography>
              <Typography variant="text-4" translation={null}>
                {line.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>

      <Modal
        open={paymentModalOpen}
        onClose={closeManualPaymentModal}
        title="Add ticket payment"
        titleTranslation="reservations"
        maxWidth="xs"
        fullWidth
        dontCloseAfterSubmit
        className="ticket-payment-modal"
        actions={[
          {
            id: 'cancel',
            label: 'Cancel',
            translation: 'reservations',
            onClick: closeManualPaymentModal,
            disabled: manualPaymentSaving,
          },
          {
            id: 'submit',
            label: 'Save',
            translation: 'reservations',
            variant: 'primary',
            onClick: handleManualPaymentSubmit,
            loading: manualPaymentSaving,
            disabled: manualPaymentSaving,
          },
        ]}
      >
        <Box className="ticket-payment-modal__content">
          <Typography variant="text-4" translation="reservations" color="subdued">
            Payment type
          </Typography>
          <Box className="ticket-payment-modal__types">
            <Checkbox
              radio
              checked={manualPaymentType === 'cash'}
              onChange={() => {
                setManualPaymentType('cash');
                resetVoucherFlow();
              }}
              label="Local / cash payment"
              translation="reservations"
            />
            <Checkbox
              radio
              checked={manualPaymentType === 'external'}
              onChange={() => {
                setManualPaymentType('external');
                resetVoucherFlow();
              }}
              label="Paid outside Gastronaut"
              translation="reservations"
            />
            <Checkbox
              radio
              checked={manualPaymentType === 'voucher'}
              onChange={() => {
                setManualPaymentType('voucher');
                resetVoucherFlow();
              }}
              label="Voucher"
              translation="reservations"
            />
            <Checkbox
              radio
              checked={manualPaymentType === 'coupon'}
              onChange={() => {
                setManualPaymentType('coupon');
                resetVoucherFlow();
              }}
              label="Discount"
              translation="reservations"
            />
          </Box>

          <TextField
            fullWidth
            label="Amount"
            labelTranslation="reservations"
            value={manualPaymentAmount}
            onChange={(event) => {
              setManualPaymentAmount(event.target.value);
              setVoucherValidation(null);
            }}
          />

          <TextField
            fullWidth
            label={
              manualPaymentType === 'voucher'
                ? 'Voucher code'
                : 'Reference or note'
            }
            labelTranslation="reservations"
            value={manualPaymentReference}
            onChange={(event) => {
              setManualPaymentReference(event.target.value);
              setVoucherValidation(null);
            }}
          />

          {manualPaymentType === 'voucher' && (
            <>
              <Button
                variant="secondary"
                translation="reservations"
                onClick={validateManualVoucher}
                loading={voucherValidationLoading}
                disabled={voucherValidationLoading}
                typographyProps={{ variant: 'text-4' }}
              >
                Validate voucher
              </Button>
              {!!voucherValidation && (
                <Box className="ticket-payment-modal__voucher-status">
                  <Typography variant="text-4" translation={null} block>
                    {t('Voucher covers amount', {
                      amount: toCurrencyString(
                        voucherValidation.appliedAmount,
                        '€',
                        false,
                        1
                      ),
                    })}
                  </Typography>
                  {voucherValidation.remainingAmount > 0.009 && (
                    <Typography variant="text-4" translation={null} block>
                      {t('Remaining amount needs another payment', {
                        amount: toCurrencyString(
                          voucherValidation.remainingAmount,
                          '€',
                          false,
                          1
                        ),
                      })}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}

          {manualPaymentType !== 'voucher' && (
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Note"
              labelTranslation="reservations"
              value={manualPaymentNote}
              onChange={(event) => setManualPaymentNote(event.target.value)}
            />
          )}

          {voucherValidation && voucherNeedsRemainder && (
            <Box className="ticket-payment-modal__remainder">
              <Typography variant="text-4" translation="reservations" color="subdued">
                Remaining payment
              </Typography>
              <Box className="ticket-payment-modal__types">
                <Checkbox
                  radio
                  checked={remainderPaymentType === 'cash'}
                  onChange={() => setRemainderPaymentType('cash')}
                  label="Local / cash payment"
                  translation="reservations"
                />
                <Checkbox
                  radio
                  checked={remainderPaymentType === 'external'}
                  onChange={() => setRemainderPaymentType('external')}
                  label="Paid outside Gastronaut"
                  translation="reservations"
                />
                <Checkbox
                  radio
                  checked={remainderPaymentType === 'coupon'}
                  onChange={() => setRemainderPaymentType('coupon')}
                  label="Discount"
                  translation="reservations"
                />
              </Box>
              <Typography variant="text-4" translation={null} block>
                {toCurrencyString(
                  voucherValidation.remainingAmount,
                  '€',
                  false,
                  1
                )}
              </Typography>
              <TextField
                fullWidth
                label="Reference or note"
                labelTranslation="reservations"
                value={remainderPaymentReference}
                onChange={(event) =>
                  setRemainderPaymentReference(event.target.value)
                }
              />
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Note"
                labelTranslation="reservations"
                value={remainderPaymentNote}
                onChange={(event) => setRemainderPaymentNote(event.target.value)}
              />
            </Box>
          )}

          {signatureRequired && (
            <SignatureDropdown
              value={manualPaymentSignature}
              onChange={setManualPaymentSignature}
              titleHasMargin
            />
          )}

          {!!manualPaymentError && (
            <Typography
              variant="text-4"
              translation={null}
              className="ticket-payment-modal__error"
              block
            >
              {manualPaymentError}
            </Typography>
          )}
        </Box>
      </Modal>

      <Modal
        open={keepModalOpen}
        onClose={() => {
          if (!keepSaving) setKeepModalOpen(false);
        }}
        title="Keep ticket reservation"
        titleTranslation="reservations"
        maxWidth="xs"
        fullWidth
        dontCloseAfterSubmit
        className="ticket-payment-modal"
        actions={[
          {
            id: 'cancel',
            label: 'Cancel',
            translation: 'reservations',
            onClick: () => setKeepModalOpen(false),
            disabled: keepSaving,
          },
          {
            id: 'submit',
            label: 'Keep reservation',
            translation: 'reservations',
            variant: 'primary',
            onClick: handleKeepReservation,
            loading: keepSaving,
            disabled: keepSaving,
          },
        ]}
      >
        <Box className="ticket-payment-modal__content">
          <Typography variant="text-3" translation="reservations" block>
            Keeping this reservation changes the ticket to payment at restaurant.
          </Typography>
          <Typography variant="text-4" translation="reservations" block>
            The open amount has to be collected later and a confirmation email will be sent.
          </Typography>
          {!!keepError && (
            <Typography
              variant="text-4"
              translation={null}
              className="ticket-payment-modal__error"
              block
            >
              {keepError}
            </Typography>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default TicketInformationV02;
